import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'

import { getLinkInBioPostStepType } from 'components/utils'
import { PayoutType, STEP_TYPE_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { parseBackendDate } from 'services/dates'
import { State } from 'state'
import { selectTaskPlatformContentTypesByName, selectTaskSetting } from 'state/constants'
import { EngagementId, StepState, TaskState } from 'state/tasks'
import { EditorValues as CustomEditorValues } from 'components/Campaign/Editor/TaskEditor/CustomTaskEditor/types'
import { CampaignState } from 'state/campaigns'

interface DynamicTaskStateProps {
    includedSteps: { [stepType: string]: boolean }
    additionalPayoutIds: string[]
}

interface EditorValues extends Omit<TaskState, 'steps'> {
    stepData: _.Dictionary<StepState>
    payouts: Record<string, PayoutObject>
    steps?: StepState[]
}

interface PayoutObject {
    cost: number | string | null
    min?: string | number | null
    max?: string | number | null
}

type UseStepEditorInitialValues = (
    campaign: CampaignState | null,
    task: TaskState,
) => EditorValues | CustomEditorValues | undefined

export const useStepEditorInitialValues: UseStepEditorInitialValues = (campaign, task) => {
    const taskSettings = useSelector((state: State) =>
        selectTaskSetting(state, { taskType: task ? task.type : undefined }),
    )
    const taskPlatformContentTypesByName = useSelector(selectTaskPlatformContentTypesByName)

    const dynamicTaskState: DynamicTaskStateProps = {
        includedSteps: {},
        additionalPayoutIds: [],
    }
    const { steps, ...nonStepTaskDetails } = task
    const stepsByType = _.keyBy(steps, 'type')
    const stepsInTask = Object.keys(stepsByType)
    const linkInBioPostStepType = getLinkInBioPostStepType(task.type)
    const engagementPayoutTypes = taskSettings.config.payout_types
        .filter(payoutType => payoutType.is_engagement)
        .map(payoutType => payoutType.id)

    const getInitialValues = (task: TaskState): EditorValues => {
        const basePayoutObject = taskSettings.config.payout_types.reduce(
            (memo: Record<string, PayoutObject>, payoutType: PayoutType) => {
                const base: PayoutObject = {
                    cost: '',
                    min: '',
                    max: '',
                }
                if (payoutType.no_counter) {
                    delete base.min
                    delete base.max
                }
                if (payoutType.id === 'base') {
                    base.cost = null
                }
                memo[payoutType.id] = base
                return memo
            },
            {},
        )
        let editorValues: EditorValues = Object.assign({}, nonStepTaskDetails, {
            payouts: Object.assign({}, basePayoutObject, task.payouts),
            stepData: stepsInTask.reduce((data: _.Dictionary<StepState>, stepType: string) => {
                if (linkInBioPostStepType && stepType.includes(linkInBioPostStepType)) {
                    data[stepType].data = Object.assign({}, data[stepType].data, task.data)
                }
                const base = {
                    [stepType]: {
                        ...data[stepType],
                        time_window: { start: null, end: null },
                    },
                }
                return data[stepType].time_window ? data : Object.assign({}, data, base)
            }, stepsByType),
        })
        //todo: combine below assign after all task support engagement data
        if (
            task.type === TASK_TYPE_ENUM.IG_STORY ||
            task.type === TASK_TYPE_ENUM.IG_STORY_V02 ||
            task.type === TASK_TYPE_ENUM.IG_POST ||
            task.type === TASK_TYPE_ENUM.IG_POST_V02 ||
            task.type === TASK_TYPE_ENUM.TIKTOK_POST ||
            task.type === TASK_TYPE_ENUM.FB_POST_V02 ||
            task.type === TASK_TYPE_ENUM.IG_REEL ||
            task.type === TASK_TYPE_ENUM.XIAOHONGSHU_POST
        ) {
            let enabledEngagements
            if (task.type === TASK_TYPE_ENUM.IG_STORY || task.type === TASK_TYPE_ENUM.IG_POST) {
                // old task type don't have checkbox. needs this initial setting to pass yup engagementMetrics validation (needs at least one checkbox be checked).
                // it's will not affect payout editor's field value validation
                enabledEngagements = [{ value: '' }]
            } else {
                enabledEngagements = Object.keys(task.payouts)
                    .filter(payoutType => engagementPayoutTypes.includes(payoutType))
                    .map(payoutType => ({ value: payoutType }))
            }

            editorValues = Object.assign({}, editorValues, {
                payouts: Object.assign({}, editorValues.payouts, {
                    engagementMetrics: enabledEngagements,
                }),
            })
        }

        if (task.type === TASK_TYPE_ENUM.CUSTOM) {
            const initialStepValues: StepState[] = task.steps.map((step: StepState) => {
                const startTime = step?.time_window?.start
                const endTime = step?.time_window?.end
                if (!startTime && !endTime) {
                    return step
                }
                return {
                    ...step,
                    time_window: {
                        start: startTime ? parseBackendDate(startTime, campaign?.country) : '',
                        end: endTime
                            ? parseBackendDate(endTime, campaign?.country, {
                                  showTime: false,
                                  showYear: true,
                                  addSeconds: -1,
                              })
                            : '',
                    },
                }
            })

            const customTaskEngagementPayoutTypes = task.platform_content_type
                ? taskPlatformContentTypesByName[task.platform_content_type].engagement_types.map(item => item.id)
                : []

            editorValues.steps = initialStepValues
            editorValues.payouts = Object.assign({}, basePayoutObject, task.payouts, {
                engagementMetrics: Object.keys(task.payouts)
                    .filter(
                        payoutType =>
                            customTaskEngagementPayoutTypes.includes(payoutType as EngagementId) &&
                            Object.keys(task.payouts[payoutType as never]).some(
                                key => task.payouts[payoutType as never][key] !== '',
                            ),
                    )
                    .map(payoutType => ({ value: payoutType })),
            })
        }
        return editorValues
    }

    const initialValues = useMemo((): EditorValues | undefined => {
        if (!campaign) return
        const initialValues = getInitialValues(task)
        const initialStepDataValues: _.Dictionary<StepState> = {}

        taskSettings.config.steps.forEach((stepType: string) => {
            if (!initialValues.stepData.hasOwnProperty(stepType)) return
            if (dynamicTaskState.includedSteps[stepType] === false && stepType !== STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO)
                return
            if (initialValues.stepData[stepType].time_window === null) return
            const startTime = initialValues.stepData[stepType]?.time_window?.start
            const endTime = initialValues.stepData[stepType]?.time_window?.end

            initialStepDataValues[stepType] = {
                ...initialValues.stepData[stepType],
                time_window: {
                    start: startTime ? parseBackendDate(startTime, campaign.country) : '',
                    end: endTime
                        ? parseBackendDate(endTime, campaign.country, {
                              showTime: false,
                              showYear: true,
                              addSeconds: -1,
                          })
                        : '',
                },
            }
        })

        let showSchedulePostTime = false
        if (
            task.type === TASK_TYPE_ENUM.FB_POST ||
            task.type === TASK_TYPE_ENUM.FB_POST_V02 ||
            task.type === TASK_TYPE_ENUM.IG_POST ||
            task.type === TASK_TYPE_ENUM.IG_POST_V02 ||
            task.type === TASK_TYPE_ENUM.IG_STORY ||
            task.type === TASK_TYPE_ENUM.IG_STORY_V02 ||
            task.type === TASK_TYPE_ENUM.TIKTOK_POST ||
            task.type === TASK_TYPE_ENUM.IG_REEL ||
            task.type === TASK_TYPE_ENUM.XIAOHONGSHU_POST
        ) {
            showSchedulePostTime = true
        }

        if (showSchedulePostTime && initialValues.data.add_post_time === undefined) {
            // set schedule post time initial value
            initialValues.data.add_post_time = false
        }

        const updatedInitialValues = {
            ...initialValues,
            stepData: initialStepDataValues,
        }

        return updatedInitialValues
    }, [task, taskSettings, taskPlatformContentTypesByName])

    return initialValues
}
